// RentaScan — Bibliothèque de décors thématiques restauration
// (SVG inline, sans dépendance).

export const BRAND = {
  ink: "#0B1F33",
  ink2: "#0B1F33",
  blue: "#1769E0",
  blueHi: "#2878FF",
  blueSoft: "#DCEBFF",
  orange: "#FF9F1C",
  orangeHi: "#FFD166",
  orangeDark: "#F58F06",
  bg: "#F4F5EF",
  text: "#111111",
  text2: "#5E6875",
  border: "#CDD7E3",
  gain: "#24845B",
  danger: "#D94A4A",
};

// Small monogram used across auth / topbar / hero
export function Logo({ size = 32, filled = true, className = "" }) {
  return (
    <div
      className={`rounded-md flex items-center justify-center ${className}`}
      style={{
        width: size, height: size,
        background: filled ? BRAND.ink : "transparent",
        border: filled ? "none" : `1.5px solid ${BRAND.ink}`,
      }}
    >
      <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="none">
        <path d="M4 20V4h4l4 8 4-8h4v16" stroke={filled ? "#FF9F1C" : BRAND.ink} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="19" cy="18" r="1.5" fill={filled ? "#FFD166" : BRAND.ink} />
      </svg>
    </div>
  );
}

// Faux invoice/ticket receipt — used as decoration
export function ReceiptSketch({ w = 260, className = "", tone = "light" }) {
  const dark = tone === "dark";
  const paper = dark ? "#F8FAFF" : "#FFFFFF";
  const text = "#0B1F33";
  const orange = "#FF9F1C";
  return (
    <svg viewBox="0 0 260 320" width={w} height={w * 1.23} className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <filter id="rec-sh" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#0B1F33" floodOpacity="0.22" />
        </filter>
      </defs>
      <g filter="url(#rec-sh)">
        {/* Zig-zag top */}
        <path d="M0 12 L10 4 L20 12 L30 4 L40 12 L50 4 L60 12 L70 4 L80 12 L90 4 L100 12 L110 4 L120 12 L130 4 L140 12 L150 4 L160 12 L170 4 L180 12 L190 4 L200 12 L210 4 L220 12 L230 4 L240 12 L250 4 L260 12 L260 300 L250 308 L240 300 L230 308 L220 300 L210 308 L200 300 L190 308 L180 300 L170 308 L160 300 L150 308 L140 300 L130 308 L120 300 L110 308 L100 300 L90 308 L80 300 L70 308 L60 300 L50 308 L40 300 L30 308 L20 300 L10 308 L0 300 Z"
          fill={paper} />
        {/* Header */}
        <rect x="18" y="28" width="90" height="10" rx="2" fill={text} opacity="0.85" />
        <rect x="18" y="44" width="60" height="7" rx="2" fill={text} opacity="0.4" />
        {/* Ligne items */}
        {[70, 90, 110, 130, 150, 170, 190, 210].map((y, i) => (
          <g key={y}>
            <rect x="18" y={y} width={90 + (i % 3) * 20} height="6" rx="1.5" fill={text} opacity={0.6} />
            <rect x="200" y={y} width={i === 3 ? 40 : 30} height="6" rx="1.5" fill={text} opacity={0.9} />
          </g>
        ))}
        {/* Highlight ligne (variation) */}
        <rect x="14" y="126" width="232" height="12" rx="3" fill={orange} opacity="0.28" />
        {/* Divider */}
        <line x1="18" y1="230" x2="242" y2="230" stroke={text} strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
        {/* Total */}
        <rect x="18" y="244" width="60" height="8" rx="2" fill={text} opacity="0.9" />
        <rect x="180" y="240" width="62" height="14" rx="2" fill={text} />
        {/* Barcode */}
        <g transform="translate(18,268)">
          {Array.from({ length: 42 }).map((_, i) => (
            <rect key={i} x={i * 5.5} y="0" width={i % 3 === 0 ? 2.6 : i % 4 === 0 ? 1 : 1.6} height="18" fill={text} />
          ))}
        </g>
      </g>
    </svg>
  );
}

// Faux price tag — used in hero / feature slots
export function PriceTag({ w = 130, label = "Coppa AOP", price = "18,56 €", tone = "orange" }) {
  const c = tone === "orange" ? "#FF9F1C" : "#1769E0";
  return (
    <svg viewBox="0 0 220 96" width={w} height={w * 0.44} xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M20 6 L200 6 A14 14 0 0 1 214 20 L214 76 A14 14 0 0 1 200 90 L20 90 L2 48 Z" fill={c} />
      <circle cx="24" cy="48" r="5" fill="#FFF" />
      <text x="60" y="42" fontFamily="Inter, Arial, sans-serif" fontSize="13" fontWeight="600" fill="#0B1F33">{label}</text>
      <text x="60" y="72" fontFamily="Inter, Arial, sans-serif" fontSize="20" fontWeight="800" fill="#0B1F33">{price}</text>
    </svg>
  );
}

// Faux small menu card
export function MenuCard({ w = 220, className = "" }) {
  return (
    <svg viewBox="0 0 220 280" width={w} height={w * 1.27} className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <filter id="mc-sh" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#0B1F33" floodOpacity="0.25" />
        </filter>
      </defs>
      <g filter="url(#mc-sh)">
        <rect x="0" y="0" width="220" height="280" rx="6" fill="#FFFFFF" />
        <rect x="0" y="0" width="220" height="42" fill="#0B1F33" />
        <text x="14" y="27" fontFamily="Inter" fontSize="13" fontWeight="700" fill="#FF9F1C">CARTE · Bistro</text>
        {/* Section title */}
        <text x="14" y="66" fontFamily="Inter" fontSize="11" letterSpacing="1.5" fill="#5E6875" fontWeight="600">ENTRÉES</text>
        <line x1="14" y1="72" x2="205" y2="72" stroke="#CDD7E3" strokeWidth="1" />
        {[
          ["Planche coppa & burrata", "18,50 €"],
          ["Velouté de courge", "9,50 €"],
          ["Œuf parfait, chorizo", "12,00 €"],
        ].map(([n, p], i) => (
          <g key={i} transform={`translate(14, ${90 + i * 24})`}>
            <text fontFamily="Inter" fontSize="11.5" fill="#111111" fontWeight="600">{n}</text>
            <text x="150" fontFamily="Inter" fontSize="11.5" fill="#111111" fontWeight="700">{p}</text>
          </g>
        ))}
        <text x="14" y="180" fontFamily="Inter" fontSize="11" letterSpacing="1.5" fill="#5E6875" fontWeight="600">PLATS</text>
        <line x1="14" y1="186" x2="205" y2="186" stroke="#CDD7E3" strokeWidth="1" />
        {[
          ["Bavette, échalotes", "22,00 €"],
          ["Risotto safrané", "19,50 €"],
        ].map(([n, p], i) => (
          <g key={i} transform={`translate(14, ${204 + i * 24})`}>
            <text fontFamily="Inter" fontSize="11.5" fill="#111111" fontWeight="600">{n}</text>
            <text x="150" fontFamily="Inter" fontSize="11.5" fill="#111111" fontWeight="700">{p}</text>
          </g>
        ))}
        {/* highlight new price */}
        <rect x="10" y="102" width="200" height="14" fill="#FF9F1C" opacity="0.25" rx="2" />
      </g>
    </svg>
  );
}

// Scan line animation for OCR panels
export function ScanBeam({ className = "" }) {
  return (
    <div className={`absolute inset-x-0 h-[2px] pointer-events-none ${className}`} style={{
      background: "linear-gradient(90deg, transparent, #FFD166, #FF9F1C, #FFD166, transparent)",
      boxShadow: "0 0 10px #FFD166",
      animation: "scan-beam 2.2s ease-in-out infinite",
    }}>
      <style>{`@keyframes scan-beam { 0%{ top:8%; opacity:0 } 15%{opacity:1} 85%{opacity:1} 100%{ top:92%; opacity:0 } }`}</style>
    </div>
  );
}
